import { useState } from "react";
import { toast } from "sonner";
import { Phone, Send, MessageCircle } from "lucide-react";
import { api } from "@/lib/api";
import { CLINICS, DOCTOR } from "@/lib/data";

const waLink = (p) => `https://wa.me/91${p.replace(/\D/g, "").slice(-10)}?text=${encodeURIComponent("Hello Dr. Chandrani Jha, I'd like to book an appointment.")}`;

const initial = { name: "", phone: "", email: "", preferred_date: "", clinic: CLINICS[0].name, message: "" };

export default function BookingForm() {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error("Please share at least your name and phone number.");
      return;
    }
    setLoading(true);
    try {
      const payload = { ...form, email: form.email.trim() || undefined };
      await api.post("/appointments", payload);
      toast.success("Thank you! Your booking request was received. We'll call you shortly to confirm.");
      setForm(initial);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Something went wrong. Please call the clinic directly.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="book" data-testid="booking-section" className="section bg-forest text-linen relative overflow-hidden">
      <div aria-hidden className="absolute -top-32 -right-32 w-[480px] h-[480px] rounded-full bg-terracotta/20 blur-3xl" />
      <div aria-hidden className="absolute -bottom-40 -left-32 w-[420px] h-[420px] rounded-full bg-sage/20 blur-3xl" />

      <div className="container-page relative grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
        <div className="lg:col-span-5">
          <p className="label-eyebrow text-linen/70">Book an Appointment</p>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl tracking-tight mt-5 leading-[1.05]">
            Reserve a quiet, <span className="italic text-terracotta">unhurried</span> consultation.
          </h2>
          <p className="mt-6 text-linen/75 text-lg leading-relaxed max-w-md">
            Share a few details and we will call you back to confirm your appointment. Prefer to talk first? Phone bookings are warmly welcome too.
          </p>

          <div className="mt-10 space-y-4">
            {DOCTOR.phones.map((p) => (
              <div key={p} className="flex items-center gap-3">
                <a href={`tel:${p.replace(/\s/g, "")}`} data-testid={`book-phone-${p.replace(/\s/g, "")}`} className="inline-flex items-center gap-3 group">
                  <span className="w-12 h-12 rounded-full border border-linen/20 flex items-center justify-center group-hover:bg-terracotta group-hover:border-terracotta transition-colors">
                    <Phone className="w-5 h-5" strokeWidth={1.5} />
                  </span>
                  <span>
                    <span className="block text-xs tracking-[0.2em] uppercase text-linen/50">Direct line</span>
                    <span className="block font-serif text-xl">{p}</span>
                  </span>
                </a>
                <a
                  href={waLink(p)}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={`book-wa-${p.replace(/\s/g, "")}`}
                  aria-label={`WhatsApp ${p}`}
                  className="inline-flex items-center gap-2 rounded-full bg-[#25D366]/15 text-[#25D366] hover:bg-[#25D366] hover:text-white transition-colors h-10 px-4 text-sm font-medium"
                >
                  <MessageCircle className="w-4 h-4" strokeWidth={1.75} /> WhatsApp
                </a>
              </div>
            ))}
          </div>
        </div>

        <div className="lg:col-span-7">
          <form onSubmit={onSubmit} data-testid="booking-form" className="bg-linen text-forest rounded-3xl p-6 md:p-10 border border-sand">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
              <label className="block">
                <span className="text-xs tracking-[0.2em] uppercase text-sage">Full Name *</span>
                <input data-testid="form-name" required name="name" value={form.name} onChange={onChange} type="text" placeholder="e.g. Sneha Roy" className="input-soft w-full mt-2" />
              </label>
              <label className="block">
                <span className="text-xs tracking-[0.2em] uppercase text-sage">Phone *</span>
                <input data-testid="form-phone" required name="phone" value={form.phone} onChange={onChange} type="tel" placeholder="+91 ..." className="input-soft w-full mt-2" />
              </label>
              <label className="block">
                <span className="text-xs tracking-[0.2em] uppercase text-sage">Email (optional)</span>
                <input data-testid="form-email" name="email" value={form.email} onChange={onChange} type="email" placeholder="you@email.com" className="input-soft w-full mt-2" />
              </label>
              <label className="block">
                <span className="text-xs tracking-[0.2em] uppercase text-sage">Preferred Date</span>
                <input data-testid="form-date" name="preferred_date" value={form.preferred_date} onChange={onChange} type="date" className="input-soft w-full mt-2" />
              </label>
              <label className="block md:col-span-2">
                <span className="text-xs tracking-[0.2em] uppercase text-sage">Clinic</span>
                <select data-testid="form-clinic" name="clinic" value={form.clinic} onChange={onChange} className="input-soft w-full mt-2 appearance-none">
                  {CLINICS.map((c) => <option key={c.id} value={c.name}>{c.name} — Birpara</option>)}
                </select>
              </label>
              <label className="block md:col-span-2">
                <span className="text-xs tracking-[0.2em] uppercase text-sage">Tell us briefly</span>
                <textarea data-testid="form-message" name="message" value={form.message} onChange={onChange} rows={4} placeholder="Reason for visit, any concerns..." className="input-soft w-full mt-2 h-auto py-3 resize-none" />
              </label>
            </div>

            <button
              data-testid="form-submit"
              type="submit"
              disabled={loading}
              className="btn-primary mt-6 w-full md:w-auto disabled:opacity-60 disabled:cursor-not-allowed"
            >
              <Send className="w-4 h-4" strokeWidth={1.5} /> {loading ? "Sending..." : "Request Appointment"}
            </button>
            <p className="mt-4 text-xs text-forest/60">By submitting, you agree to be contacted by the clinic at the number provided. We respect your privacy.</p>
          </form>
        </div>
      </div>
    </section>
  );
}
